<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div class="chips-container" :class="{ 'p-invalid': validation?.$error || modelstate }">
    <ul class="chips-list" role="listbox" :aria-describedby="`${id}-help`">
      <li
        v-for="(chip, index) in chips"
        :key="index"
        class="chips-chip"
        role="option"
        :aria-label="`Chip: ${chip}`"
      >
        <span class="chips-chip-label">{{ chip }}</span>
        <button
          v-if="!disabled"
          type="button"
          class="chips-chip-remove"
          @click="removeChip(index)"
          :disabled="disabled"
          aria-label="Remove chip"
        >
          ×
        </button>
      </li>
      <li class="chips-input-wrapper">
        <input
          ref="chipInput"
          type="text"
          :id="id"
          :placeholder="chips.length ? '' : placeholder"
          :disabled="disabled || (max && chips.length >= max)"
          class="chips-input"
          @input="handleInput"
          @keydown="handleKeydown"
          @blur="handleBlur"
          aria-autocomplete="list"
          role="textbox"
        />
      </li>
    </ul>
    <span v-if="modelstate" class="p-error">
      {{ modelstate }}
    </span>
    <span
      v-if="
        validation?.$error &&
        validation.required &&
        validation.required.$invalid
      "
      :id="`${id}-help`"
      class="p-error text-left mt-2"
    >
      {{ validation.required.$message.replace("Value", PropName) }}
    </span>
    <span
      v-else-if="
        validation?.$error &&
        validation.requiredIf &&
        validation.requiredIf.$invalid
      "
      :id="`${id}-help`"
      class="p-error text-left mt-2"
    >
      {{ validation.requiredIf.$message.replace("value", PropName) }}
    </span>
    <span
      v-else-if="
        validation?.$error &&
        validation.max &&
        validation.max.$invalid
      "
      :id="`${id}-help`"
      class="p-error text-left mt-2"
    >
      {{ validation.max.$message.replace("value", PropName) }}
    </span>
  </div>
</template>

<script>
export default {
  props: {
    id: {
      type: String,
      default: "",
    },
    modelValue: {
      type: Array,
      default: () => [],
    },
    placeholder: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    validation: {
      type: Object,
      required: false,
    },
    modelstate: {
      type: String,
      default: "",
    },
    PropName: {
      type: String,
      default: "Chips",
    },
    max: {
      type: Number,
      default: null,
    },
    separator: {
      type: String,
      default: ",",
    },
    allowDuplicate: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      chips: [],
      inputValue: "",
    };
  },
  watch: {
    modelValue: {
      handler(newValue) {
        this.chips = Array.isArray(newValue) ? [...newValue] : [];
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    handleInput(event) {
      this.inputValue = event.target.value;
    },
    handleKeydown(event) {
      if (event.key === "Enter" || event.key === this.separator) {
        event.preventDefault();
        this.addChip(this.inputValue);
      } else if (event.key === "Backspace" && !this.inputValue && this.chips.length) {
        this.removeChip(this.chips.length - 1);
      }
    },
    handleBlur() {
      if (this.inputValue) {
        this.addChip(this.inputValue);
      }
    },
    addChip(value) {
      const trimmedValue = value.trim();
      if (!trimmedValue || (!this.allowDuplicate && this.chips.includes(trimmedValue))) {
        this.inputValue = "";
        return;
      }
      if (this.max && this.chips.length >= this.max) {
        return;
      }
      const newChips = [...this.chips, trimmedValue];
      this.chips = newChips;
      this.inputValue = "";
      this.$refs.chipInput.value = "";
      this.$emit("update:modelValue", newChips);
    },
    removeChip(index) {
      const newChips = this.chips.filter((_, i) => i !== index);
      this.chips = newChips;
      this.$emit("update:modelValue", newChips);
      this.$refs.chipInput.focus();
    },
  },
};
</script>

<style lang="scss" scoped>
// input:enabled:focus {
//   outline: 0 none;
//   outline-offset: 0;
//   box-shadow: none !important;
//   border: #c0c0bc !important;
//   outline: 2px solid #222222 !important;
//   border-style: double !important;
// }
.chips-container {
  display: inline-flex;
  align-items: center;
  background-color: #ffffff;
  border: 1px solid #ced4da;
  border-radius: 6px;
  padding: 0.25rem;
  min-height: 2.5rem;
  width: 100%;
  box-sizing: border-box;
  font-family: "voyado Text", sans-serif;
  font-size: 1rem;
}

.chips-container:focus-within {
  outline: 0 none;
  outline-offset: 0;
  box-shadow: none !important;
  border: #c0c0bc !important;
  outline: 2px solid #222222 !important;
  border-style: double !important;
}

.chips-container.p-invalid {
  border-color: #dc3545;
}

.chips-list {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  list-style: none;
  margin: 0;
  padding: 0;
  gap: 0.25rem;
  width: 100%;
}

.chips-chip {
  display: inline-flex;
  align-items: center;
  background-color: #eceef1;
  border: 1px solid #d1d5db;
  border-radius: 16px;
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #21211f;
}

.chips-chip-label {
  margin-right: 0.25rem;
}

.chips-chip-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1rem;
  height: 1rem;
  padding: 0;
  border: none;
  background: none;
  color: #21211f;
  font-size: 1rem;
  line-height: 1;
  cursor: pointer;
  transition: color 0.2s;
}

// .chips-chip-remove:hover:not(:disabled) {
//   color: #dc3545;
// }

// .chips-chip-remove:focus {
//   outline: 2px solid #6366f1;
//   outline-offset: 1px;
//   border-radius: 50%;
// }

.chips-input-wrapper {
  display: inline-flex;
  align-items: center;
  flex-grow: 1;
  min-width: 100px;
}

.chips-input {
  border: none;
  outline: none;
  background: transparent;
  font-size: 1rem;
  font-family: "voyado Text", sans-serif;
  font-weight: 400;
  color: #21211f;
  padding: 0.25rem;
  width: 100%;
}

.chips-input:disabled {
  background: transparent;
  cursor: not-allowed;
  opacity: 0.6;
}

.p-error {
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
}
</style>